import { getQwantum } from 'qwanyx';

class TemplateCache {
    constructor() {
        this.cache = new Map();
    }

    async fetchTemplate(vi, forceRefresh = false) {
        // Force refresh or check if we should refresh in development
        if (forceRefresh || this.shouldRefresh(vi)) {
            this.cache.delete(vi);
        }
        
        // Check cache first (if not force refreshed)
        if (this.cache.has(vi)) {
            return this.cache.get(vi);
        }
        
        try {
            // FIXED: Fetch the card JSON using getQwantum
            const cardJson = await getQwantum(vi, 'card');
            if (!cardJson) {
                throw new Error(`No card data found for vi: ${vi}`);
            }
            
            // FIXED: cardJson is already the JSON string, don't parse it
            // The card data is the template JSON string with ${item.xxx} variables
            const template = cardJson; // This is already a JSON string
            
            // Cache it as string
            this.cache.set(vi, template);
            return template;
        } catch (error) {
            console.error(`Error fetching template for vi ${vi}:`, error);
            throw error;
        }
    }

    shouldRefresh(vi) {
        // In development mode, always refresh templates for better developer experience
        // This ensures template changes are immediately visible
        return process.env.NODE_ENV === 'development';
    }

    replaceTemplateVariables(templateJsonString, itemData) {
        if (!templateJsonString || !itemData) {
            return templateJsonString;
        }

        try {
            // FIXED: Work directly with the JSON string
            let processedJson = templateJsonString;
            
            console.log(`[TemplateCache] Processing template for item:`, {
                templateLength: templateJsonString.length,
                itemDataKeys: Object.keys(itemData),
                hasQwantum: !!itemData.qwantum,
                sampleItemData: { 
                    name: itemData.name,
                    title: itemData.title,
                    firstname: itemData.firstname
                }
            });
            
            // NEW: Enhanced variable replacement with nested access support
            const getValue = (path, data) => {
                if (!path || !data) return null;
                
                // Handle dot notation for nested properties
                const keys = path.split('.');
                let value = data;
                
                for (const key of keys) {
                    if (value && typeof value === 'object' && key in value) {
                        value = value[key];
                    } else {
                        return null;
                    }
                }
                
                return value;
            };
            
            // Process ${item.xxx} placeholders with enhanced path support
            processedJson = processedJson.replace(/\$\{item\.([^}]+)\}/g, (match, path) => {
                // Try direct property access first
                let value = getValue(path, itemData);
                
                // If not found and we have qwantum data, try qwantum access
                if ((value === null || value === undefined) && itemData.qwantum) {
                    value = getValue(path, itemData.qwantum);
                }
                
                // Convert value to string if valid
                if (value !== null && value !== undefined && value !== '') {
                    const replacementValue = typeof value === 'string' ? value : String(value);
                    console.log(`[TemplateCache] Replaced ${match} with "${replacementValue}"`);
                    return replacementValue;
                } else {
                    console.log(`[TemplateCache] Removed empty placeholder ${match}`);
                    return '';
                }
            });
            
            // Legacy support: Process top-level properties for backward compatibility
            Object.keys(itemData).forEach(key => {
                const placeholder = `\${item.${key}}`;
                const value = itemData[key];
                
                // Only replace if value exists and is not empty/null/undefined
                if (value !== null && value !== undefined && value !== '' && typeof value !== 'object') {
                    // Handle different data types
                    const replacementValue = typeof value === 'string' ? value : String(value);
                    
                    // Count replacements for debugging
                    const beforeLength = processedJson.length;
                    processedJson = processedJson.replaceAll(placeholder, replacementValue);
                    const afterLength = processedJson.length;
                    
                    if (beforeLength !== afterLength) {
                        console.log(`[TemplateCache] Legacy replaced ${placeholder} with "${replacementValue}"`);
                    }
                }
            });
            
            // Process qwantum properties if available
            if (itemData.qwantum && typeof itemData.qwantum === 'object') {
                Object.keys(itemData.qwantum).forEach(key => {
                    const placeholder = `\${item.${key}}`;
                    const value = itemData.qwantum[key];
                    
                    if (value !== null && value !== undefined && value !== '' && typeof value !== 'object') {
                        const replacementValue = typeof value === 'string' ? value : String(value);
                        
                        const beforeLength = processedJson.length;
                        processedJson = processedJson.replaceAll(placeholder, replacementValue);
                        const afterLength = processedJson.length;
                        
                        if (beforeLength !== afterLength) {
                            console.log(`[TemplateCache] Qwantum replaced ${placeholder} with "${replacementValue}"`);
                        }
                    }
                });
            }
            
            // NEW: Clean up any remaining unreplaced placeholders
            // This handles cases where template has ${item.xyz} but itemData doesn't have 'xyz' property
            processedJson = processedJson.replace(/\$\{item\.[^}]+\}/g, '');
            
            // FIXED: Return the processed JSON string (not parsed object)
            return processedJson;
        } catch (error) {
            console.error('Error processing template variables:', error);
            return templateJsonString; // Return original template if processing fails
        }
    }

    // NEW: Advanced replacement with conditional logic
    replaceTemplateVariablesAdvanced(templateJsonString, itemData) {
        if (!templateJsonString || !itemData) {
            return templateJsonString;
        }

        try {
            let processedJson = templateJsonString;
            
            console.log(`[TemplateCache] Advanced processing template for item:`, {
                templateLength: templateJsonString.length,
                itemDataKeys: Object.keys(itemData)
            });
            
            // Handle conditional blocks: {{if item.field}}content{{/if}}
            processedJson = processedJson.replace(/\{\{if item\.([^}]+)\}\}(.*?)\{\{\/if\}\}/gs, (match, fieldName, content) => {
                const value = itemData[fieldName];
                if (value !== null && value !== undefined && value !== '') {
                    return content;
                }
                return '';
            });
            
            // Handle conditional blocks with fallback: {{item.field || "fallback"}}
            processedJson = processedJson.replace(/\{\{item\.([^|]+)\|\|([^}]+)\}\}/g, (match, fieldName, fallback) => {
                const value = itemData[fieldName.trim()];
                if (value !== null && value !== undefined && value !== '') {
                    return typeof value === 'string' ? value : String(value);
                }
                return fallback.trim().replace(/^["']|["']$/g, ''); // Remove quotes from fallback
            });
            
            // Standard replacement for existing data
            Object.keys(itemData).forEach(key => {
                const placeholder = `\${item.${key}}`;
                const value = itemData[key];
                
                if (value !== null && value !== undefined && value !== '') {
                    const replacementValue = typeof value === 'string' ? value : String(value);
                    processedJson = processedJson.replaceAll(placeholder, replacementValue);
                    console.log(`[TemplateCache] Replaced ${placeholder} with "${replacementValue}"`);
                } else {
                    processedJson = processedJson.replaceAll(placeholder, '');
                    console.log(`[TemplateCache] Removed empty placeholder ${placeholder}`);
                }
            });
            
            // Clean up any remaining unreplaced placeholders
            processedJson = processedJson.replace(/\$\{item\.[^}]+\}/g, '');
            
            return processedJson;
        } catch (error) {
            console.error('Error processing template variables (advanced):', error);
            return templateJsonString;
        }
    }

    clearCache() {
        this.cache.clear();
    }

    removeFromCache(vi) {
        this.cache.delete(vi);
    }

    getCacheSize() {
        return this.cache.size;
    }
}

// Singleton instance
let templateCacheInstance = null;

export const getTemplateCache = () => {
    if (!templateCacheInstance) {
        templateCacheInstance = new TemplateCache();
    }
    return templateCacheInstance;
};

export default TemplateCache;